/**
 * What the create-case dialog needs before it can draw its tabs: whether the
 * request service is connected (the "From NextRequest" tab, FB-1007 #7) and which
 * eDiscovery systems are connected. Both rarely change, so the cases page loads
 * them when it opens and they're kept for the session: the dialog shows the kept
 * answer at once (no gray-to-white flip on every open) and re-checks in the
 * background.
 */
import { intakeApi } from '../../lib/api';
import type { EDiscoveryCaseSummary, EDiscoverySystem, RequestServiceStatus } from '../../types';

let requestService: RequestServiceStatus | null = null;
let systems: EDiscoverySystem[] | null = null;
let pending: Promise<void> | null = null;

/** The request service's last known status, or null before the first answer. */
export function knownRequestService(): RequestServiceStatus | null {
  return requestService;
}

/** The last known connected eDiscovery systems, or null before the first answer. */
export function knownEDiscoverySystems(): EDiscoverySystem[] | null {
  return systems;
}

/**
 * Load (or re-check) both. Calls made while one is running share it. Never
 * rejects: a failed check keeps the last known answer, or falls back to "not
 * connected" / no systems when there is none yet.
 */
export function loadIntakeStatus(): Promise<void> {
  pending ??= Promise.all([
    intakeApi.getRequestService().then(
      (status) => { requestService = status; },
      () => { requestService ??= { state: 'not_connected' }; },
    ),
    intakeApi.listEDiscoverySystems().then(
      (available) => { systems = available; },
      () => { systems ??= []; },
    ),
  ]).then(() => undefined).finally(() => { pending = null; });
  return pending;
}

/**
 * The eDiscovery system's case list, asked for when the dialog opens: Purview
 * takes 5–10 s to list cases, so the dialog starts it on open and the case
 * picker (Data collection step) uses the same request. A request made in the
 * last minute is reused, so a repeated effect (React StrictMode runs them twice
 * in development) doesn't list cases twice; a later open lists them afresh. A
 * failed list is never reused.
 */
const CASE_LIST_REUSE_MS = 60_000;
const caseLists = new Map<EDiscoverySystem, { at: number; cases: Promise<EDiscoveryCaseSummary[]> }>();

export function prefetchEDiscoveryCases(system: EDiscoverySystem): Promise<EDiscoveryCaseSummary[]> {
  const kept = caseLists.get(system);
  if (kept && Date.now() - kept.at < CASE_LIST_REUSE_MS) return kept.cases;
  const entry = { at: Date.now(), cases: intakeApi.searchEDiscoveryCases(system, '') };
  entry.cases.catch(() => {
    // The picker shows the error if it uses this list; the next open asks again.
    if (caseLists.get(system) === entry) caseLists.delete(system);
  });
  caseLists.set(system, entry);
  return entry.cases;
}

/** The dialog closed without unmounting: the next open lists cases afresh. */
export function forgetEDiscoveryCases(): void {
  caseLists.clear();
}

/** Tests only: forget what's known. */
export function resetIntakeStatus(): void {
  requestService = null;
  systems = null;
  pending = null;
  caseLists.clear();
}
